import { useState, useEffect, useCallback } from "react";
import {
  _get_admin_zones,
  _get_admin_zone_detail,
  _create_admin_zone,
  _update_admin_zone,
  _delete_admin_zone,
  _assign_support_manager_to_zone,
  _assign_support_officers_to_zone,
  _unassign_support_officer_from_zone,
  _assign_restaurants_to_support_officer,
  _unassign_restaurant_from_support_officer,
  _get_admin_available_support_officers,
  _get_zone_restaurants,
} from "../../../common/axios_services";

const useZoneManagement = () => {
  const [zones, setZones] = useState([]);
  const [zoneDetail, setZoneDetail] = useState(null);
  const [availableSOs, setAvailableSOs] = useState([]);
  const [zoneRestaurants, setZoneRestaurants] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [pagination, setPagination] = useState({});

  const fetchZones = useCallback(async (params = {}) => {
    try {
      setLoading(true);
      setError(null);
      const res = await _get_admin_zones(params);
      setZones(res?.data?.data?.data || res?.data?.data || []);
      setPagination(res?.data?.pagination || {});
      return { success: true, data: res?.data?.data };
    } catch (err) {
      const errorMessage = err.message || "Failed to load zones";
      setError(errorMessage);
      return { success: false, error: errorMessage };
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchZoneDetail = useCallback(async (zoneId) => {
    try {
      setLoading(true);
      setError(null);
      const res = await _get_admin_zone_detail(zoneId);
      setZoneDetail(res?.data?.data || {});
      return { success: true, data: res?.data?.data };
    } catch (err) {
      const errorMessage = err.message || "Failed to load zone detail";
      setError(errorMessage);
      return { success: false, error: errorMessage };
    } finally {
      setLoading(false);
    }
  }, []);

  const createZone = useCallback(
    async (zoneData) => {
      try {
        setLoading(true);
        setError(null);
        await _create_admin_zone(zoneData);
        await fetchZones();
        return { success: true };
      } catch (err) {
        const errorMessage = err.message || "Failed to create zone";
        setError(errorMessage);
        return { success: false, error: errorMessage };
      } finally {
        setLoading(false);
      }
    },
    [fetchZones],
  );

  const updateZone = useCallback(
    async (zoneId, zoneData) => {
      try {
        setLoading(true);
        setError(null);
        await _update_admin_zone(zoneId, zoneData);
        await fetchZones();
        await fetchZoneDetail(zoneId);
        return { success: true };
      } catch (err) {
        const errorMessage = err.message || "Failed to update zone";
        setError(errorMessage);
        return { success: false, error: errorMessage };
      } finally {
        setLoading(false);
      }
    },
    [fetchZones, fetchZoneDetail],
  );

  const deleteZone = useCallback(
    async (zoneId) => {
      try {
        setLoading(true);
        setError(null);
        await _delete_admin_zone(zoneId);
        await fetchZones();
        return { success: true };
      } catch (err) {
        const errorMessage = err.message || "Failed to delete zone";
        setError(errorMessage);
        return { success: false, error: errorMessage };
      } finally {
        setLoading(false);
      }
    },
    [fetchZones],
  );

  const assignSMToZone = useCallback(
    async (zoneId, supportManagerId) => {
      try {
        setLoading(true);
        setError(null);
        await _assign_support_manager_to_zone(zoneId, supportManagerId);
        await fetchZones();
        await fetchZoneDetail(zoneId);
        return { success: true };
      } catch (err) {
        const errorMessage = err.message || "Failed to assign support manager";
        setError(errorMessage);
        return { success: false, error: errorMessage };
      } finally {
        setLoading(false);
      }
    },
    [fetchZones, fetchZoneDetail],
  );

  const fetchAvailableSOs = useCallback(async (zoneId) => {
    try {
      setLoading(true);
      const res = await _get_admin_available_support_officers({ zoneId });
      setAvailableSOs(res?.data?.data || []);
      return { success: true, data: res?.data?.data };
    } catch (err) {
      console.error("Error fetching available SOs:", err);
      return { success: false, error: err.message };
    } finally {
      setLoading(false);
    }
  }, []);

  const assignSOsToZone = useCallback(
    async (zoneId, supportOfficerIds) => {
      try {
        setLoading(true);
        setError(null);
        await _assign_support_officers_to_zone(zoneId, supportOfficerIds);
        await fetchZones();
        await fetchZoneDetail(zoneId);
        await fetchAvailableSOs(zoneId);
        return { success: true };
      } catch (err) {
        const errorMessage = err.message || "Failed to assign support officers";
        setError(errorMessage);
        return { success: false, error: errorMessage };
      } finally {
        setLoading(false);
      }
    },
    [fetchZones, fetchZoneDetail, fetchAvailableSOs],
  );

  const unassignSOFromZone = useCallback(
    async (zoneId, soId) => {
      try {
        setLoading(true);
        setError(null);
        await _unassign_support_officer_from_zone(zoneId, soId);
        await fetchZones();
        await fetchZoneDetail(zoneId);
        return { success: true };
      } catch (err) {
        const errorMessage =
          err.message || "Failed to unassign support officer";
        setError(errorMessage);
        return { success: false, error: errorMessage };
      } finally {
        setLoading(false);
      }
    },
    [fetchZones, fetchZoneDetail],
  );

  const assignRestaurantsToSO = useCallback(
    async (soId, restaurantIds, zoneId) => {
      try {
        setLoading(true);
        setError(null);
        await _assign_restaurants_to_support_officer(
          soId,
          restaurantIds,
          zoneId,
        );
        await fetchZoneRestaurants(zoneId);
        await fetchZoneDetail(zoneId);
        return { success: true };
      } catch (err) {
        const errorMessage = err.message || "Failed to assign restaurants";
        setError(errorMessage);
        return { success: false, error: errorMessage };
      } finally {
        setLoading(false);
      }
    },
    [fetchZoneDetail],
  );

  const fetchZoneRestaurants = useCallback(async (zoneId, params = {}) => {
    try {
      setLoading(true);
      const res = await _get_zone_restaurants(zoneId, params);
      setZoneRestaurants(res?.data?.data || []);
      return { success: true, data: res?.data?.data };
    } catch (err) {
      console.error("Error fetching zone restaurants:", err);
      return { success: false, error: err.message };
    } finally {
      setLoading(false);
    }
  }, []);

  const unassignRestaurantFromSO = useCallback(
    async (soId, rtId, zoneId) => {
      try {
        setLoading(true);
        setError(null);
        await _unassign_restaurant_from_support_officer(soId, rtId);
        await fetchZoneRestaurants(zoneId);
        await fetchZoneDetail(zoneId);
        return { success: true };
      } catch (err) {
        const errorMessage = err.message || "Failed to unassign restaurant";
        setError(errorMessage);
        return { success: false, error: errorMessage };
      } finally {
        setLoading(false);
      }
    },
    [fetchZoneRestaurants, fetchZoneDetail],
  );

  useEffect(() => {
    fetchZones();
  }, [fetchZones]);

  return {
    zones,
    zoneDetail,
    availableSOs,
    zoneRestaurants,
    loading,
    error,
    pagination,
    fetchZones,
    fetchZoneDetail,
    createZone,
    updateZone,
    deleteZone,
    assignSMToZone,
    assignSOsToZone,
    fetchAvailableSOs,
    unassignSOFromZone,
    assignRestaurantsToSO,
    fetchZoneRestaurants,
    unassignRestaurantFromSO,
  };
};

export default useZoneManagement;
